import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ENV_CONFIG } from '@ps/core-config';
import {
  CompanyBuyerConfigBulkSaveResponseDto,
  CompanyBuyerConfigResetResponseDto,
  CompanyBuyerConfigResponseDto,
  CompanyBuyerCountrySettingsResponseDto,
} from './dtos/company-buyer-config.dto';
import { CompanyBuyerConfigMapper } from './mappers/company-buyer-config.mapper';
import { CompanyBuyerConfigPayloadMapper } from './mappers/company-buyer-config-payload.mapper';
import {
  BuyerConfigSettingsForm,
  BuyerConfigSettingsSavePayload,
  BuyerCountrySettingsSavePayload,
} from '../models/company-buyer-config.model';

@Injectable({ providedIn: 'root' })
export class CompanyBuyerConfigApi {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentConfig = inject(ENV_CONFIG);
  private readonly _baseUrl = this._environmentConfig.pureSpectrum.url;
  private readonly _mapper = new CompanyBuyerConfigMapper();
  private readonly _payloadMapper = new CompanyBuyerConfigPayloadMapper();

  public buyerConfigSettings(
    buyerId: number,
  ): Observable<BuyerConfigSettingsForm> {
    return this._httpClient
      .get<CompanyBuyerConfigResponseDto>(
        `${this._baseUrl}/buyers/v2/configs/${buyerId}`,
      )
      .pipe(map((response) => this._mapper.fromDto(response)));
  }

  public saveBuyerConfigSettings(
    payload: BuyerConfigSettingsSavePayload,
  ): Observable<string> {
    const body = this._payloadMapper.toPayload(payload);

    return this._httpClient
      .put<CompanyBuyerConfigBulkSaveResponseDto>(
        `${this._baseUrl}/buyerConfigSettings/${payload.configSettingsId}/bulk-update`,
        body,
      )
      .pipe(map((response) => response.msg));
  }

  public saveBuyerCountrySettings(
    payload: BuyerCountrySettingsSavePayload,
  ): Observable<string> {
    const body = this._payloadMapper.toCountrySettingsPayload(payload);

    return this._httpClient
      .put<CompanyBuyerCountrySettingsResponseDto>(
        `${this._baseUrl}/buyerSettings/${payload.buyerId}`,
        body,
      )
      .pipe(map((response) => response.msg));
  }

  public resetToDefault(buyerId: number): Observable<string> {
    return this._httpClient
      .put<CompanyBuyerConfigResetResponseDto>(
        `${this._baseUrl}/reset-to-default/${buyerId}`,
        {},
      )
      .pipe(map((response) => response.msg));
  }
}
